import { faTrashCan, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMediaQuery } from '@mantine/hooks';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BASE_URL } from '../../../api/EndPoints';
import { createB2BAPI } from '../../../api/Interceptor';
import { getpayLoadFromToken } from '../../common/JwtPayload';
import Navigation from '../sidebar/Navigation';
import './WishListDetails.css';

const WishListDetails = () => {
    const isMobile = useMediaQuery('(max-width: 767px)');
    const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 991px)');
    const isLessThanLaptop = useMediaQuery('(max-width: 1199px)');
    const isGreaterThanLaptop = useMediaQuery('(min-width: 1200px)');
    const [wishList, setWishList] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [showToast, setShowToast] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [yardQuantities, setYardQuantities] = useState({});
    const [cartTypes, setCartTypes] = useState({});
    const [cartItem, setCartItem] = useState([]);
    const [inputValue, setInputValue] = useState({});
    const [stocks, setStocks] = useState({});
    const payload = useMemo(() => getpayLoadFromToken(), []);
    const userRole = payload?.ROLE;
    const navigate = useNavigate();
    const B2B_API = useMemo(() => createB2BAPI(navigate), [navigate]);


    const groupedWishList = _.groupBy(wishList, 'pim.product.articleCode');

    useEffect(() => {
        fetchWishList();
    }, []);

    const fetchWishList = async () => {
        try {
            const res = await B2B_API.get('wishlist/all').json();
            setWishList(res.response);
            if (res.response && res.response.length > 0) {
                fetchStocksForWishlist(res.response);
            }
        } catch (error) {
            console.error("Error fetching wishlist:", error);
        }
    };

    const fetchStocksForWishlist = async (items) => {
        const skuIds = items.map((item) => item?.pim?.pimVariants?.[0]?.variantSku).filter(Boolean);
        if (skuIds.length === 0) return;
        try {
            const res = await B2B_API.get(`stock/price?pimVariantSku=${skuIds.join(',')}`).json();
            setStocks(res.response || {});
        } catch (error) {
            console.error("Error fetching stock for wishlist:", error);
        }
    };

    const deleteSelectedItemsFromWishList = async (selectedSkus) => {
        if (selectedSkus.length === 0) return;
        try {
            const skuString = selectedSkus.join(',');
            await B2B_API.delete(`wishlist/delete?id=${skuString}`).json();
            fetchWishList();
        } catch (error) {
            console.error("Error deleting items from wishlist:", error);
        }
    };

    const deleteAllItemsFromWishList = async () => {
        try {
            await B2B_API.delete('wishlist/delete-all').json();
            fetchWishList();
        } catch (error) {
            console.error("Error deleting all items from wishlist:", error);
        }
    };


    const handleClickOutside = (event) => {
        if (event.target.classList.contains('modal-backdrop')) {
            setShowModal(false);
        }
    };

    useEffect(() => {
        if (showModal) {
            document.addEventListener('click', handleClickOutside);
        } else {
            document.removeEventListener('click', handleClickOutside);
        }

        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [showModal]);


    const handleDelete = () => {
        if (selectedItems.length === 0) {
            setShowToast(true);
            setTimeout(() => setShowToast(false), 3000);
            return;
        }
        setShowModal(true);
    };

    const confirmDelete = async () => {
        try {
            setShowModal(false);
            if (selectAll) {
                await deleteAllItemsFromWishList();
            } else {
                await deleteSelectedItemsFromWishList(selectedItems);
            }
            fetchWishList();
            setSelectedItems([]);
        } catch (error) {
            alert("Failed to delete items from wishlist.");
        }
    };
    const handleXmarkDelete = async (id) => {
        try {
            await deleteSelectedItemsFromWishList([id]);
        } catch (error) {
            console.error("Failed to delete item:", error);
        }
    };

    const handleXmarkclick = () => {
        setShowModal(false);
    }

    const handleSelectAll = (e) => {
        const checked = e.target.checked;
        setSelectAll(checked);
        if (checked) {
            const allVariantId = wishList.map((item) => item.id);
            setSelectedItems(_.uniq(allVariantId));
        } else {
            setSelectedItems([]);
        }
    };



    const handleItemSelect = (id) => {
        setSelectedItems((prevSelectedItems) => {
            if (prevSelectedItems.includes(id)) {
                return prevSelectedItems.filter(item => item !== id);
            } else {
                return [...prevSelectedItems, id];
            }
        });
    };



    const CartOption = (wishlist) => {
        if (!wishlist || wishlist?.length === 0) {
            console.warn("Wishlist data is undefined or empty");
            return [];
        }
        const firstVariant = wishlist[0]?.pim?.pimVariants?.[0];
        const samplemoq = firstVariant?.sampleMOQ;
        const wholesalemoq = firstVariant?.wholesaleMOQ;
        return [
            { label: 'Sample', value: String(samplemoq || '') },
            { label: 'Wholesale', value: String(wholesalemoq || '') }
        ];
    };

    const cartOptions = CartOption(wishList);


    const isItemInCart = (pimVariantId, cartType = null) => {
        return Array.isArray(cartItem) && cartItem.some(item =>
            item.pimVariantId === pimVariantId &&
            (cartType ? item.cartType === cartType : true)
        );
    };

    const isSampleInCart = (pimVariantId) => isItemInCart(pimVariantId, 'SAMPLE');

    const getButtonLabel = (pimItem) => {
        const qty = inputValue[pimItem.id];
        if (!qty) {
            return 'Add to Sample Cart';
        }
        if (isSampleInCart(pimItem.pimVariantId) && cartTypes[pimItem.id] === 'SAMPLE') {
            return 'Already in Sample Cart';
        }
        return cartTypes[pimItem.id] === 'WHOLESALE' ? 'Add to Wholesale Cart' : 'Add to Sample Cart';
    };

    const isButtonDisabled = (pimItem) => {
        return getButtonLabel(pimItem) === 'Already in Sample Cart';
    };


    const handleYardQuantityChange = (id, wholesaleMOQ, e) => {
        const qty = e.target.value;
        setYardQuantities(prevQuantities => ({ ...prevQuantities, [id]: qty }));
        setInputValue(prevValues => ({ ...prevValues, [id]: qty }));
        setCartTypes(prevCartTypes => ({
            ...prevCartTypes, [id]: parseInt(qty, 10) >= wholesaleMOQ ? 'WHOLESALE' : 'SAMPLE'
        }));
    };

    const handleAddToCart = async (pimItem) => {
        const token = localStorage.getItem('token');
        if (!token) {
            console.error('Error: User is not logged in.');
            return;
        }

        const quantity = parseInt(yardQuantities[pimItem.id], 10);
        const cartType = cartTypes[pimItem.id];

        if (!quantity || quantity <= 0) {
            console.error('Error: Invalid quantity.');
            return;
        }

        const cartItem = {
            pimVariantId: pimItem.pimVariantId,
            cartType: cartType,
            quantity: quantity,
        };
        try {
            await B2B_API.post('cart/save', { json: cartItem });
            await fetchCarts();
            navigate('/cart');
        } catch (error) {
            console.error('Error adding to cart:', error);
        }
    };

    useEffect(() => {
        fetchCarts();
    }, []);

    const fetchCarts = async () => {
        try {
            const response = await B2B_API.get('cart/getall').json();
            setCartItem(response.response);
        } catch (error) {
            console.error('Error fetching cart:', error);
        }
    };



    return (
        userRole === 'Customer' && (
            <div className="view-my-page router-view-container">
                {(!isMobile && !isTablet) && <Navigation />}
                <div className="view-wishlists route-view">
                    <div className="header">
                        <div className="page-title flex-wrap">Wishlist</div>
                    </div>
                    {wishList?.length === 0 ? (
                        <div className="text-center m-t-96 f-13 f-500">There are no items.</div>
                    ) : (
                        <div className="wish-list-products">
                            <div className="flex-row flex-between items-center default-padding">
                                <div className="flex-row items-center cursor-pointer no-select g-8" >
                                    <input
                                        type="checkbox"
                                        className="app-checkbox flex-wrap no-touch"
                                        style={{ width: '16px', height: '16px' }}
                                        checked={selectAll}
                                        onChange={(e) => handleSelectAll(e)}

                                    />
                                    <div className="f-13 f-500">Select All</div>
                                </div>
                                <button className="btn btn-brd dark c-black f-12 g-4" onClick={handleDelete}>
                                    <FontAwesomeIcon className="fa-trash-alt" icon={faTrashCan} />
                                    Delete
                                </button>
                            </div>

                            {/* Single flex container with nested quality-groups */}
                            <div className="flex g-20">
                                {_.map(groupedWishList, (wishlistRows, articleCode) => {
                                    const firstPim = wishlistRows[0]?.pim;
                                    const items = wishlistRows.map(row => {
                                        const variant = row.pim?.pimVariants?.[0];
                                        if (!variant) return null;
                                        const colorVariant = variant.variants?.find(v => v.type === 'Colour');
                                        return {
                                            id: row.id,
                                            pimVariantId: variant.id,
                                            variantSku: variant.variantSku,
                                            image: variant.image,
                                            sellingPrice: variant.sellingPrice,
                                            hexaColorCode: colorVariant?.hexaColorCode,
                                            value: colorVariant?.value,
                                            samplemoq: variant.sampleMOQ,
                                            wholesalemoq: variant.wholesaleMOQ,
                                            sampleLeadTime: firstPim?.pimOtherInformation?.sampleLeadTime,
                                            wholesaleLeadTime: firstPim?.pimOtherInformation?.wholesaleLeadTime,
                                            pimUrl: firstPim?.landingUrl
                                        };
                                    }).filter(Boolean);
                                    return (
                                        <div className="quality-group default-padding" key={articleCode}>
                                            <div className="flex-row flex-between items-center m-b-12">
                                                <div className="flex-row items-center cursor-pointer no-select g-8" >
                                                    <input
                                                        type="checkbox"
                                                        className="app-checkbox flex-wrap no-touch"
                                                        style={{ width: '16px', height: '16px' }}
                                                        checked={items.every(item => selectedItems.includes(item.id))}
                                                        onChange={() => {
                                                            const allids = items.map(item => item?.id);
                                                            const isAllSelected = allids.every(id => selectedItems.includes(id));
                                                            const newSelectedItems = isAllSelected ? selectedItems.filter(id => !allids.includes(id)) : [...selectedItems, ...allids];
                                                            setSelectedItems(_.uniq(newSelectedItems));
                                                        }}
                                                    />
                                                    <div className="text-underline f-700" onClick={() => { navigate(firstPim?.landingUrl) }}>{firstPim?.product?.articleName || articleCode}</div>
                                                </div>
                                            </div>

                                            <div className="flex g-12">
                                                {items.map((pimItem, index) => (
                                                isLessThanLaptop && (
                                                    <div className="wish-list-product" key={index}>
                                                        <div className="flex-row flex-between">
                                                            <input
                                                                type="checkbox"
                                                                className="app-checkbox flex-wrap"
                                                                style={{ width: '16px', height: '16px' }}
                                                                checked={selectedItems.includes(pimItem.id)}
                                                                onChange={() => handleItemSelect(pimItem?.id)}

                                                            />
                                                            <div className="move-and-delete">
                                                                <div className="btn-move absolute-function"></div>
                                                                <FontAwesomeIcon
                                                                    icon={faXmark}
                                                                    className="fal fa-times center absolute-function"
                                                                    onClick={() => handleXmarkDelete(pimItem?.id)}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="flex-fill">
                                                            <div className="adaptive-layout boundary-width-1200" style={{ gap: '24px', display: 'flex', justifyContent: 'space-between' }}>
                                                                <div className="flex-row items-center g-16"  onClick={() => navigate(`${items[0].pimUrl}?variantId=${pimItem.variantSku}`)} >
                                                                    <div className="image-container flex-wrap">
                                                                        <img className="app-img cover" src={`${BASE_URL}${pimItem.image?.replace("/api", "")}?time=${Date?.now()}`} alt={pimItem?.variantSku} loading="lazy" />
                                                                    </div>
                                                                    <div className="lcn-container">
                                                                        {/* <div className="pd-code" >
                                                                            <span className="clickable-area" onClick={() => navigate(`${items[0].pimUrl}?variantId=${pimItem.variantSku}`)}>{pimItem?.variantSku || 'Unknown'}</span>
                                                                        </div> */}
                                                                        <div className="label-color-number">
                                                                            <div className="color-dot" style={{ background: pimItem?.hexaColorCode }}></div>
                                                                            <span>#{index + 1}</span>
                                                                            <span>∙</span>
                                                                            <span style={{ fontSize: '14px' }}>{pimItem?.value || 'N/A'}</span>
                                                                        </div>
                                                                        <div style={{ fontSize: '14px' }}>
                                                                            {stocks[pimItem.variantSku] && (stocks[pimItem.variantSku]?.wholeSaleQuantity ?? 0) >= 1 ? (
                                                                                <span>Available Wholesale Qty : {stocks[pimItem.variantSku]?.wholeSaleQuantity?.toFixed(2)} kgs</span>
                                                                            ) : (
                                                                                <span>Wholesale Lead Time : {pimItem?.wholesaleLeadTime || '-'}</span>
                                                                            )}
                                                                        </div>
                                                                        <div style={{ fontSize: '14px' }}>
                                                                            {stocks[pimItem.variantSku] && Math.round(stocks[pimItem.variantSku]?.sampleQuantity ?? 0) >= 1 ? (
                                                                                <span>Available Sample Qty : {Math.round(stocks[pimItem.variantSku]?.sampleQuantity)} kgs</span>
                                                                            ) : (
                                                                                <span>Sample Lead Time : {pimItem?.sampleLeadTime || '-'}</span>
                                                                            )}
                                                                        </div>
                                                                        <div className="price-summary">&#8377;{pimItem?.sellingPrice?.toFixed(2)}</div>
                                                                    </div>
                                                                </div>
                                                                <div className="wish-list-product-functions">
                                                                    <div className="range-selector flex-wrap">
                                                                        <div className="input-wrappers flex-row items-center">
                                                                            <input
                                                                                placeholder="1+"
                                                                                min="1"
                                                                                type="number"
                                                                                pattern="\d*"
                                                                                maxLength="5"
                                                                                style={{ border: 'none', padding: '0px' }}
                                                                                value={yardQuantities[pimItem.id] || ''}
                                                                                onChange={(e) => handleYardQuantityChange(pimItem.id, pimItem.wholesalemoq, e)}
                                                                            />
                                                                            <span className="flex-wrap c-gray-400 f-13 f-500">kgs</span>
                                                                        </div>
                                                                    </div>
                                                                    <button
                                                                        className={!isButtonDisabled(pimItem) ? "btn btn-primary" : "btn-disabled"}
                                                                        disabled={isButtonDisabled(pimItem)}
                                                                        onClick={() => handleAddToCart(pimItem)}
                                                                    >
                                                                        {getButtonLabel(pimItem)}
                                                                    </button>

                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                 )
                                             ))}

                                            {/* Map over items under the same variantSku */}
                                            {items.map((pimItem, index) => (
                                                console.log(pimItem, "uuu"),
                                                isGreaterThanLaptop && (
                                                    <div className="wish-list-product" key={index}>
                                                        <div className="flex-row flex-between">
                                                            <input
                                                                type="checkbox"
                                                                className="app-checkbox flex-wrap"
                                                                style={{ width: '16px', height: '16px' }}
                                                                checked={selectedItems.includes(pimItem.id)}
                                                                onChange={() => handleItemSelect(pimItem?.id)}

                                                            />
                                                        </div>

                                                        <div className="flex-fill">
                                                            <div className="adaptive-layout boundary-width-1200"  style={{ gap: '24px', display: 'flex', justifyContent: 'space-between' }}>
                                                                <div className="flex-row items-center g-16" onClick={() => navigate(`${items[0].pimUrl}?variantId=${pimItem.variantSku}`)}>
                                                                    <div className="image-container flex-wrap">
                                                                        <img className="app-img cover"
                                                                            src={`${BASE_URL}${pimItem.image?.replace("/api", "")}?time=${Date?.now()}`}
                                                                            alt={pimItem.pimUrl?.split("/fabrics/")[1]}

                                                                            loading="lazy" />
                                                                    </div>
                                                                    <div className="lcn-container">
                                                                        {/* <div className="pd-code">
                                                                            <span className="clickable-area" onClick={() => navigate(`${items[0].pimUrl}?variantId=${pimItem.variantSku}`)}>{pimItem?.variantSku || 'Unknown'}</span>
                                                                        </div> */}
                                                                        <div className="label-color-number">
                                                                            <div className="color-dot" style={{ background: pimItem?.hexaColorCode }}></div>
                                                                            <span>#{index + 1}</span>
                                                                            <span>∙</span>
                                                                            <span
                                                                                style={{ fontSize: '14px' }}
                                                                                title={pimItem?.value || 'N/A'}
                                                                            >
                                                                                {(pimItem?.value || 'N/A').length > 10
                                                                                    ? (pimItem?.value || 'N/A').slice(0, 10) + '...'
                                                                                    : (pimItem?.value || 'N/A')}
                                                                            </span>
                                                                        </div>
                                                                        <div style={{ fontSize: '14px' }}>
                                                                            {stocks[pimItem.variantSku] && (stocks[pimItem.variantSku]?.wholeSaleQuantity ?? 0) >= 1 ? (
                                                                                <span>Available Wholesale Qty : {stocks[pimItem.variantSku]?.wholeSaleQuantity?.toFixed(2)} kgs</span>
                                                                            ) : (
                                                                                <span>Wholesale Lead Time : {pimItem?.wholesaleLeadTime || '-'}</span>
                                                                            )}
                                                                        </div>
                                                                        <div style={{ fontSize: '14px' }}>
                                                                            {stocks[pimItem.variantSku] && Math.round(stocks[pimItem.variantSku]?.sampleQuantity ?? 0) >= 1 ? (
                                                                                <span>Available Sample Qty : {Math.round(stocks[pimItem.variantSku]?.sampleQuantity)} kgs</span>
                                                                            ) : (
                                                                                <span>Sample Lead Time : {pimItem?.sampleLeadTime || '-'}</span>
                                                                            )}
                                                                        </div>
                                                                        <div className="price-summary">&#8377; {pimItem?.sellingPrice?.toFixed(2) || '-'}</div>
                                                                    </div>
                                                                </div>

                                                                <div className="wish-list-product-functions">
                                                                    <div className="range-selector flex-wrap">
                                                                        <div className="input-wrappers flex-row items-center">
                                                                            <input
                                                                                placeholder="1+"
                                                                                min="1"
                                                                                type="number"
                                                                                pattern="\d*"
                                                                                maxLength="5"
                                                                                style={{ border: 'none', padding: '0px' }}
                                                                                value={yardQuantities[pimItem.id] || ''}
                                                                                onChange={(e) => handleYardQuantityChange(pimItem.id, pimItem.wholesalemoq, e)}
                                                                            />
                                                                            <span className="flex-wrap c-gray-400 f-13 f-500">kgs</span>
                                                                        </div>
                                                                    </div>
                                                                    <button
                                                                        className={!isButtonDisabled(pimItem) ? "btn btn-primary" : "btn-disabled"}
                                                                        disabled={isButtonDisabled(pimItem)}
                                                                        onClick={() => handleAddToCart(pimItem)}
                                                                    >
                                                                        {getButtonLabel(pimItem)}
                                                                    </button>
                                                                </div>
                                                                <div className="move-and-delete">
                                                                    <div className="btn-move absolute-function"></div>
                                                                    <FontAwesomeIcon
                                                                        icon={faXmark}
                                                                        className="fal fa-times center absolute-function"
                                                                        onClick={() => handleXmarkDelete(pimItem?.id)}
                                                                    />
                                                                </div>

                                                            </div>
                                                        </div>

                                                    </div>
                                                )
                                            ))}

                                            {showModal && (
                                                <div class="app-modal">
                                                    <div class="modal-fixed-container">
                                                        <div class="modal-backdrop opened"></div>
                                                        <div class="modal-basic modal-base-style">
                                                            <div class="modal-header">
                                                                <div>
                                                                    <div class="title">Are you sure to delete?</div>
                                                                </div>
                                                                <FontAwesomeIcon className="far fa-times flex-wrap m-l-a" icon={faXmark} onClick={handleXmarkclick} />
                                                            </div>
                                                            <div class="modal-body pre-line">
                                                                <div class="p-l-20 p-r-20">This cannot be undone.</div>
                                                            </div>
                                                            <div class="bottom-buttons">
                                                                <button class="btn btn-primary" onClick={confirmDelete}>Delete</button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            {showToast && (
                                                <div class="app-toast">
                                                    <div class="app-toast-container error slide-down-leave-active slide-down-leave-to">
                                                        <div class="html">No items selected</div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    )
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        )
    );
};

export default WishListDetails;